/**
 * The chat list names a driver room in full - car, plates and crew, e.g.
 * "C204M PY11670 Pavel Solapau" - while the header used to show only the driver
 * resolved from the crew config. Prefer the room's own name so both read the
 * same, but only while that name still names the driver we resolved: a room
 * whose name lags behind a crew swap would otherwise put yesterday's driver in
 * the header, which is exactly what driver resolution exists to prevent.
 */
const namesResolvedDriver = (roomName: string, driverName: string): boolean => {
  const haystack = roomName.toLowerCase()
  const parts = driverName
    .toLowerCase()
    .split(/\s+/)
    // Initials and stray separators match anything, so they say nothing about
    // whose room this is.
    .filter((part) => part.length > 1)

  return parts.length > 0 && parts.every((part) => haystack.includes(part))
}

export const composeChatHeaderTitle = (
  roomName: string | undefined,
  driverTitle: string,
): string => {
  if (!roomName) return driverTitle
  if (!driverTitle) return roomName

  return namesResolvedDriver(roomName, driverTitle) ? roomName : driverTitle
}

/**
 * A driver room's vehicle label repeats the crew after the plates, e.g.
 * "KR3NC43 Gosia Kierowca", while the agent header already titles the chat
 * with the driver. Keep only the vehicle part of the label. A crew member's
 * name goes only when the label carries all of it: "GD12345 Pawel Gockowi69"
 * for Pawel Gockowitz stays whole rather than read "GD12345 Gockowi69".
 */
export const withoutCrewNames = (
  label: string,
  crewNames: string[],
): string => {
  const tokens = label.split(/\s+/)
  const present = new Set(tokens.map((token) => token.toLowerCase()))
  const named = new Set(
    crewNames
      .map((name) =>
        name
          .toLowerCase()
          .split(/\s+/)
          .filter((part) => part.length > 1),
      )
      .filter(
        (parts) => parts.length > 0 && parts.every((part) => present.has(part)),
      )
      .flat(),
  )
  if (named.size === 0) return label
  const vehicle = tokens
    .filter((token) => token !== "&" && !named.has(token.toLowerCase()))
    .join(" ")
  return vehicle || label
}
